import React, { useState, useEffect } from 'react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  Users,
  Search,
  Calendar,
  Award,
  User,
  Phone,
  Mail,
  MapPin,
  BookOpen,
  TrendingUp,
  TrendingDown,
  Minus,
  Eye,
  Download,
  Filter
} from 'lucide-react';
import StatsCard from '../../ui/StatsCard';

const StudentReports = () => {
  const { students, classes, exams, results, attendance } = useData();
  const { user } = useAuth();
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');

  // Filter students based on search and class
  const filteredStudents = students.filter(student => {
    const matchesSearch = student.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.registrationId?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesClass = selectedClass === 'all' || 
      (student.enrollment?.unitId === selectedClass || 
       student.enrollment?.unitId?._id === selectedClass || 
       student.unitId === selectedClass);

    return matchesSearch && matchesClass;
  });

  // Get student's exam results
  const getStudentResults = (studentId) => {
    return results.filter(result => result.studentId === studentId);
  };

  // Get student's attendance
  const getStudentAttendance = (studentId) => {
    return attendance.filter(record => record.studentId === studentId);
  };

  // Calculate attendance percentage
  const getAttendancePercentage = (studentId) => {
    const studentAttendance = getStudentAttendance(studentId);
    if (studentAttendance.length === 0) return 0;

    const presentDays = studentAttendance.filter(record => record.status === 'present').length;
    return Math.round((presentDays / studentAttendance.length) * 100);
  };

  // Calculate average performance
  const getAveragePerformance = (studentId) => {
    const studentResults = getStudentResults(studentId);
    if (studentResults.length === 0) return { average: 0, totalExams: 0 };

    const totalMarks = studentResults.reduce((sum, result) => sum + result.obtainedMarks, 0);
    const totalMaxMarks = studentResults.reduce((sum, result) => sum + result.totalMarks, 0);
    const average = totalMaxMarks > 0 ? Math.round((totalMarks / totalMaxMarks) * 100) : 0;

    return { average, totalExams: studentResults.length };
  };

  // Get performance trend
  const getPerformanceTrend = (studentId) => {
    const studentResults = getStudentResults(studentId);
    if (studentResults.length < 2) return 'stable';

    const recentResults = studentResults.slice(-3);
    const olderResults = studentResults.slice(-6, -3);

    if (recentResults.length === 0 || olderResults.length === 0) return 'stable';

    const recentAvg = recentResults.reduce((sum, r) => sum + (r.obtainedMarks / r.totalMarks * 100), 0) / recentResults.length;
    const olderAvg = olderResults.reduce((sum, r) => sum + (r.obtainedMarks / r.totalMarks * 100), 0) / olderResults.length;

    if (recentAvg > olderAvg + 5) return 'improving';
    if (recentAvg < olderAvg - 5) return 'declining';
    return 'stable';
  };

  const renderOverview = (student) => {
    const attendancePercentage = getAttendancePercentage(student.id);
    const performance = getAveragePerformance(student.id);
    const trend = getPerformanceTrend(student.id);

    return (
      <div className="space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Attendance</p>
                <p className="text-2xl font-bold text-gray-900">{attendancePercentage}%</p>
              </div>
              <Calendar className="h-8 w-8 text-blue-600" />
            </div>
            <div className="mt-2">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{ width: `${attendancePercentage}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Average Performance</p>
                <p className="text-2xl font-bold text-gray-900">{performance.average}%</p>
              </div>
              <Award className="h-8 w-8 text-green-600" />
            </div>
            <div className="mt-2">
              <p className="text-sm text-gray-500">{performance.totalExams} exams taken</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Performance Trend</p>
                <div className="flex items-center space-x-2">
                  {trend === 'improving' && <TrendingUp className="h-5 w-5 text-green-600" />}
                  {trend === 'declining' && <TrendingDown className="h-5 w-5 text-red-600" />}
                  {trend === 'stable' && <Minus className="h-5 w-5 text-gray-600" />}
                  <p className="text-lg font-semibold text-gray-900 capitalize">{trend}</p>
                </div>
              </div>
              <BookOpen className="h-8 w-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {getStudentResults(student.id).slice(-3).map((result, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{result.examName}</p>
                  <p className="text-sm text-gray-600">{result.subject}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">{result.obtainedMarks}/{result.totalMarks}</p>
                  <p className="text-sm text-gray-600">{Math.round((result.obtainedMarks / result.totalMarks) * 100)}%</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderPersonalDetails = (student) => {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Personal Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Full Name</label>
              <p className="mt-1 text-sm text-gray-900">{student.firstName} {student.lastName}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Registration ID</label>
              <p className="mt-1 text-sm text-gray-900">{student.registrationId}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <p className="mt-1 text-sm text-gray-900">{student.email}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <p className="mt-1 text-sm text-gray-900">{student.phone}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Class</label>
              <p className="mt-1 text-sm text-gray-900">
                {student.enrollment?.unitId?.unitName || student.enrollment?.unitName || student.unitId || '-'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
              <p className="mt-1 text-sm text-gray-900">{student.dateOfBirth}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Address</label>
              <p className="mt-1 text-sm text-gray-900">{student.address}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Parent Information</label>
              <p className="mt-1 text-sm text-gray-900">{student.parentName}</p>
              <p className="text-sm text-gray-600">{student.parentPhone}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAttendanceDetails = (student) => {
    const studentAttendance = getStudentAttendance(student.id);

    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Attendance Records</h3>

        {studentAttendance.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {studentAttendance.slice(-20).map((record, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(record.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        record.status === 'present'
                          ? 'bg-green-100 text-green-800'
                          : record.status === 'absent'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {record.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.subject || 'General'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {record.notes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No attendance records found</p>
        )}
      </div>
    );
  };

  const renderExamResults = (student) => {
    const studentResults = getStudentResults(student.id);

    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Exam Results</h3>

        {studentResults.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exam</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marks</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Percentage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {studentResults.map((result, index) => {
                  const percentage = Math.round((result.obtainedMarks / result.totalMarks) * 100);
                  const grade = percentage >= 90 ? 'A+' :
                               percentage >= 80 ? 'A' :
                               percentage >= 70 ? 'B+' :
                               percentage >= 60 ? 'B' :
                               percentage >= 50 ? 'C+' :
                               percentage >= 40 ? 'C' : 'F';

                  return (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {result.examName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {result.subject}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {result.examDate ? new Date(result.examDate).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="font-medium text-slate-900">{result.obtainedMarks}/{result.totalMarks}</div>
                        {(() => {
                           const subjectComponents = result.markComponents || result.markComponents || [];
                           if (subjectComponents.length >= 2) {
                             return (
                               <div className="mt-1 flex flex-col gap-0.5">
                                 {subjectComponents.map((comp, idx) => (
                                   <span key={idx} className="text-[10px] text-slate-500">
                                     {comp.name}: <span className="font-semibold">{comp.obtainedMarks}/{comp.maxMarks}</span>
                                   </span>
                                 ))}
                               </div>
                             );
                           }
                           return null;
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {percentage}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          percentage >= 70 ? 'bg-green-100 text-green-800' :
                          percentage >= 50 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {grade}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No exam results found</p>
        )}
      </div>
    );
  };

  const handleExportStudentReport = (student) => {
    const studentResults = getStudentResults(student.id);
    const studentAttendance = getStudentAttendance(student.id);
    const attendancePercentage = getAttendancePercentage(student.id);
    const performance = getAveragePerformance(student.id);

    const reportData = {
      student: student,
      attendance: {
        records: studentAttendance,
        percentage: attendancePercentage
      },
      results: {
        records: studentResults,
        average: performance.average,
        totalExams: performance.totalExams
      },
      generatedOn: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `student_report_${student.registrationId}_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Student Reports</h1>
            <p className="text-gray-600 mt-1">
              Comprehensive student performance and activity reports
            </p>
          </div>
          {/* <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Users className="h-4 w-4" />
            <span>{students.length} total students</span>
          </div> */}
          <StatsCard
          title="Total Students"
            value={students.length}

            icon={ <Users className="h-4 w-4" />}
            color="green"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Student Selection */}
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Student</h3>

            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search students..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Class Filter */}
            <div className="mb-4">
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Classes</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.name}</option>
                ))}
              </select>
            </div>

            {/* Student List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredStudents.map(student => (
                <div
                  key={student.id}
                  onClick={() => setSelectedStudent(student)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedStudent?.id === student.id
                      ? 'bg-primary-100 border-2 border-primary-500'
                      : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {student.firstName} {student.lastName}
                      </p>
                      <p className="text-sm text-gray-600">{student.registrationId}</p>
                      <p className="text-sm text-gray-500">
                        {student.enrollment?.unitId?.unitName || student.enrollment?.unitName || student.unitId || '-'}
                      </p>
                    </div>
                    <Eye className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Student Details */}
        <div className="lg:col-span-2">
          {selectedStudent ? (
            <div className="space-y-6">
              {/* Student Header */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedStudent.firstName} {selectedStudent.lastName}
                    </h2>
                    <p className="text-gray-600">
                      {selectedStudent.registrationId} • {selectedStudent.enrollment?.unitId?.unitName || selectedStudent.enrollment?.unitName || selectedStudent.unitId || '-'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleExportStudentReport(selectedStudent)}
                    className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    <span>Export Report</span>
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="border-b border-gray-200">
                  <nav className="flex space-x-8 px-6">
                    {[
                      { id: 'overview', label: 'Overview', icon: User },
                      { id: 'personal', label: 'Personal Details', icon: User },
                      { id: 'attendance', label: 'Attendance', icon: Calendar },
                      { id: 'results', label: 'Exam Results', icon: Award }
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                          activeTab === tab.id
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <tab.icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                <div className="p-6">
                  {activeTab === 'overview' && renderOverview(selectedStudent)}
                  {activeTab === 'personal' && renderPersonalDetails(selectedStudent)}
                  {activeTab === 'attendance' && renderAttendanceDetails(selectedStudent)}
                  {activeTab === 'results' && renderExamResults(selectedStudent)}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white p-12 rounded-lg shadow-sm border border-gray-200 text-center">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Student</h3>
              <p className="text-gray-500">
                Choose a student from the list to view their comprehensive report
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentReports;
